<template>
  <div id="app">
    <n-layout v-if="!isProjectLoading && isReady">
      <template v-slot:nav v-if="endPoint.length > 0 && endPoint !== 'login'">
        <side-bar/>
      </template>
      <template>
        <template v-if="!isDataSaving">
          <transition name="el-fade-in" mode="out-in">
            <router-view/>
          </transition>
          <transition name="el-fade-in" mode="out-in">
            <cancel-project-modal
              v-if="showCancelProjectModal"
              :visible.sync="showCancelProjectModal">
            </cancel-project-modal>
          </transition>
        </template>
        <div v-else class="loading">
          <Loader />
        </div>
      </template>
      <template v-slot:advice>
        <advice v-if="endPoint.length > 0 && endPoint !== 'login'" />
      </template>
    </n-layout>
    <div v-else class="loading">
      <Loader :text="$t('base.loading')"/>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import NLayout from '@/components/layout/Layout.vue';
import Loader from '@/components/utils/Loader.vue';

import cookie from '@/utils/helpers/cookie';
import SideBar from '@/components/navbar/SideBar.vue';
import Advice from '@/components/advice/Advice.vue';
import CancelProjectModal from '@/components/utils/modals/CancelProjectModal.vue';
import EventBus from '@/utils/helpers/event-bus';
import { Connector } from './backendConnector';
import RouteManager from '@/routes/RouteManager';
import {
  STEPS_TO_ROUTE,
  CREATED_STEP,
  COMPLETED_STEP,
} from '@/store/modules/projectStore';

export default {
  components: {
    SideBar,
    CancelProjectModal,
    Loader,
    NLayout,
    Advice,
  },
  data() {
    return {
      isReady: false,
      showCancelProjectModal: false,
    };
  },
  computed: {
    ...mapGetters({
      stepStore: 'projectStore/step',
      isProjectLoading: 'projectStore/projectLoading',
      isDataSaving: 'projectStore/isDataSaving',
      cancelStatus: 'projectStore/cancelStatus',
    }),
    endPoint() {
      return this.$route.path.split('/')[1] || '';
    },
    childPoint() {
      return this.$route.path.split('/')[2] || '';
    },
  },
  methods: {
    ...mapActions({
      getProject: 'projectStore/getProject',
      autoLogin: 'authStore/autoLogin',
      getProfile: 'authStore/getProfile',
    }),
    onResize() {
      const minWidth = window.innerWidth;
      const minHeight = window.innerHeight;
      this.$store.commit('dimensionStore/SET_DIMENSION',
        { width: minWidth, height: minHeight });
    },
    scanURL() {
      const urlParams = new URLSearchParams(window.location.search);
      const projectToken = urlParams.get('project_token');
      const authKey = urlParams.get('auth_key');
      return { projectToken, authKey };
    },
    scanUrlAndLogIn() {
      const { projectToken, authKey } = this.scanURL();
      if (projectToken && authKey) {
        return this.autoLogin({ projectToken, authKey });
      }
      return false;
    },
    getCookies() {
      const authToken = cookie.getCookie(process.env.VUE_APP_COOKIE_NAME_AUTH);
      const projectToken = cookie.getCookie(process.env.VUE_APP_COOKIE_NAME_PROJECT_ID);
      return { authToken, projectToken };
    },
    setProjectTokens(authToken, projectToken) {
      Connector.getInstance().setBearer(authToken);
      Connector.getInstance().setProjectToken(projectToken);
    },
    getAndSetCookies() {
      const { authToken, projectToken } = this.getCookies();
      this.setProjectTokens(authToken, projectToken);
    },
    isUserAlreadyLoggedIn() {
      const { authToken, projectToken } = this.getCookies();
      return authToken && projectToken;
    },
    redirectToLoginPage() {
      cookie.deleteCookie(process.env.VUE_APP_COOKIE_NAME_AUTH);
      cookie.deleteCookie(process.env.VUE_APP_COOKIE_NAME_PROJECT_ID);
      return this.$router.replace({
        name: 'login',
      });
    },
    getNavigationType() {
      try {
        const performanceNavigation = window.performance.getEntriesByType('navigation');
        return performanceNavigation[0].type;
      } catch (e) {
        return window.performance.navigation.type === 1 ? 'reload' : 'autre';
      }
    },
    redirectToCurrentStepPage() {
      this.getAndSetCookies();
      return this.getProject()
        .then(() => this.getProfile())
        .then(() => {
          const newRoute = STEPS_TO_ROUTE[this.stepStore];
          const thisStepRoute = STEPS_TO_ROUTE[RouteManager.getBaseStepRoute(this.$route.name)];

          if (this.getNavigationType() === 'reload'
            && this.$route.name !== null
            && this.stepStore !== COMPLETED_STEP
            && thisStepRoute && newRoute
            && thisStepRoute.index < newRoute.index) {
            // ONLY ACCEPT THE ROUTE IF THE INDEX IS BEFORE THE LAST SAVED STEP
            return Promise.resolve();
          }
          if (newRoute === undefined) {
            // eslint-disable-next-line consistent-return
            return this.$router.push({ name: STEPS_TO_ROUTE[CREATED_STEP].name });
          }
          if (this.$route.name !== newRoute.name) {
            // eslint-disable-next-line consistent-return
            return this.$router.push({
              name: newRoute.name,
            });
          }
          // eslint-disable-next-line consistent-return
          return Promise.resolve();
        });
    },
    redirectToLoginErrorPage(error) {
      this.$store.commit('authStore/UNSET_IS_LOGGED');
      cookie.deleteCookie(process.env.VUE_APP_COOKIE_NAME_AUTH);
      cookie.deleteCookie(process.env.VUE_APP_COOKIE_NAME_PROJECT_ID);
      return this.$router
        .replace({
          name: 'login',
          query: {
            error: error || 'AUTO_LOGIN_COOKIE_DISABLE',
          },
        });
    },
    openCancelProject() {
      this.showCancelProjectModal = !this.showCancelProjectModal;
    },
    onLoadPopup() {
      window.addEventListener('message', this.onMessagePopup);
    },
    onMessagePopup(event) {
      const nextoriesPopup = event.data && event.data.nextoriesPopup;
      if (nextoriesPopup) {
        window.removeEventListener('message', this.onMessagePopup);
        this.removePopupContainer();
        if (nextoriesPopup.clickPhone) {
          window.open(`tel:${this.$t('base.phone.link')}`);
        } else if (nextoriesPopup.clickCancel) {
          this.openCancelProject();
        }
      }
    },
    removePopupContainer() {
      const popupContainer = document.getElementsByClassName('ub-emb-container');
      if (popupContainer && popupContainer[0]) {
        popupContainer[0].remove();
      }
    },
  },
  async created() {
    this.isReady = false;
    try {
      if (!cookie.isCookieEnable()) {
        await this.redirectToLoginErrorPage();
      } else if (await this.scanUrlAndLogIn()) {
        await this.redirectToCurrentStepPage();
      } else if (this.isUserAlreadyLoggedIn()) {
        await this.redirectToCurrentStepPage();
      } else {
        await this.redirectToLoginPage();
      }
    } catch (e) {
      // eslint-disable-next-line
      console.error('Application Initialize Error', e);
      await this.redirectToLoginErrorPage(e
        && e.response
        && e.response.data
        && e.response.data.error);
    }
    this.isReady = true;
    EventBus.$on('openCancelProject', () => { this.openCancelProject(); });
  },
  mounted() {
    const externalScript = document.createElement('script');
    externalScript.setAttribute('src', process.env.VUE_APP_UNBOUNCE_POPUP_URL);
    externalScript.async = true;
    document.head.appendChild(externalScript);
    this.onResizeBinded = this.onResize.bind(this);
    this.onResize();
    this.$nextTick(() => {
      window.addEventListener('resize', this.onResizeBinded);
    });
    window.addEventListener('load', this.onLoadPopup);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResizeBinded);
    window.removeEventListener('message', this.onMessagePopup);
    window.removeEventListener('load', this.onLoadPopup);
  },
  watch: {
    cancelStatus(value) {
      if (value) {
        this.removePopupContainer();
      }
    },
  },
};
</script>


<style lang="scss">
  * {
    box-sizing: border-box;
    font-family: 'futura-pt', sans-serif;
    user-select: none;
    scrollbar-color: $--color-secondary $--color-light-grey-blue;
    scrollbar-width: thin;
  }

  input, textarea {
    user-select: text;
  }

  html, body, #app{
    color: $--color-text-primary;
    background-color: $--color-white;
    padding: 0;
    margin: 0;
    width: 100%;
    height: 100%;
    font-size: $--font-size-large;
  }

  ::-webkit-scrollbar {
    width: 5px;
    display: none;
    @include breakpoint(desktop){
      display: unset;
    }
  }

  /* Track */
  ::-webkit-scrollbar-track {
    background: $--color-light-grey-blue;
    border-radius: $--border-radius-base;
  }

  /* Handle */
  ::-webkit-scrollbar-thumb {
    background: $--color-secondary;
    border-radius: $--border-radius-base;
  }

  /* Handle on hover */
  ::-webkit-scrollbar-thumb:hover {
    background: $--color-secondary;
  }

  .loading {
    height: 100%;
    width: 100%;
  }
</style>
